import React, { useEffect, useState } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import Login from './components/Login';
import Dashboard from './components/Dashboard';
import Profile from './components/Profile';
import { getAdminProfile, getRawApiKey, clearSession, storeAdminProfile } from './utils/storage';
import RedirectHandler from './RedirectHandler';

const ProtectedRoute = ({ isAuthenticated, children }) => {
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  return children;
};

function App() {
  const [session, setSession] = useState({ admin: null, apiKey: null, avatar: null });
  const [isReady, setIsReady] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const loadSession = () => {
      const admin = getAdminProfile();
      const apiKey = getRawApiKey();
      const avatar = admin?.image_url || null;
      setSession({ admin, apiKey, avatar });
      setIsReady(true);
    };

    loadSession();

    const handleSessionExpired = () => {
      clearSession();
      setSession({ admin: null, apiKey: null, avatar: null });
      navigate('/login', { replace: true });
    };

    window.addEventListener('session-expired', handleSessionExpired);
    return () => window.removeEventListener('session-expired', handleSessionExpired);
  }, [navigate]);

  const handleAuthenticated = (profile) => {
    const avatar = profile?.image_url || null;
    setSession({ admin: profile, apiKey: getRawApiKey(), avatar });
    navigate('/dashboard', { replace: true });
  };

  const handleLogout = () => {
    clearSession();
    setSession({ admin: null, apiKey: null, avatar: null });
    navigate('/login', { replace: true });
  };

  const handleBackToDashboard = () => navigate('/dashboard');

  const handleAvatarUpdate = (imageUrl) => {
    setSession((prev) => {
      const updatedAdmin = prev.admin ? { ...prev.admin, image_url: imageUrl ?? null } : prev.admin;
      if (updatedAdmin) {
        storeAdminProfile(updatedAdmin);
      }
      return { ...prev, admin: updatedAdmin, avatar: imageUrl ?? null };
    });
  };

  const handleProfileUpdate = (updatedAdmin) => {
    if (!updatedAdmin) {
      return;
    }

    storeAdminProfile(updatedAdmin);
    setSession((prev) => {
      const nextAvatar = Object.prototype.hasOwnProperty.call(updatedAdmin, 'image_url')
        ? updatedAdmin.image_url
        : prev.avatar;
      return { ...prev, admin: updatedAdmin, avatar: nextAvatar ?? null };
    });
  };

  const isAuthenticated = Boolean(session.admin && session.apiKey);

  useEffect(() => {
    if (!isReady) {
      return;
    }

    if (isAuthenticated && location.pathname === '/login') {
      navigate('/dashboard', { replace: true });
    }
  }, [isAuthenticated, isReady, location.pathname, navigate]);

  if (!isReady) {
    return null;
  }

  return (
    <>
      <RedirectHandler />
      <Routes>
        <Route
          path="/login"
          element={
            isAuthenticated ? <Navigate to="/dashboard" replace /> : <Login onAuthenticated={handleAuthenticated} />
          }
        />

        <Route
          path="/profile"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <Profile
                admin={session.admin}
                avatar={session.avatar}
                onBack={handleBackToDashboard}
                onLogout={handleLogout}
                onAvatarChange={handleAvatarUpdate}
                onProfileUpdate={handleProfileUpdate}
              />
            </ProtectedRoute>
          }
        />

        <Route
          path="/dashboard/*"
          element={
            <ProtectedRoute isAuthenticated={isAuthenticated}>
              <Dashboard
                admin={session.admin}
                avatar={session.avatar}
                onLogout={handleLogout}
                onViewProfile={() => navigate('/profile')}
              />
            </ProtectedRoute>
          }
        />

        <Route
          path="/"
          element={<Navigate to={isAuthenticated ? '/dashboard' : '/login'} replace />}
        />

        <Route
          path="*"
          element={<Navigate to={isAuthenticated ? '/dashboard' : '/login'} replace />}
        />
      </Routes>
    </>
  );
}

export default App;
